/**
 * A greenhouse gas inventory, and the arithmetic it has to hold up under.
 *
 * The one table in a sustainability statement that has to add up. A balance
 * sheet that does not balance is caught by everybody; a scope 3 whose fifteen
 * categories do not sum to the scope 3 total is caught by nobody until an
 * assurance provider adds them up by hand. So the subtotals and the totals are
 * **derived here** and never typed, which is the whole reason this is a
 * section kind rather than a table with the right headings.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * **Scope 2 is reported twice, and the total with it.**
 *
 * The GHG Protocol requires both a location-based and a market-based figure
 * for purchased energy, and they are routinely far apart: the same electricity
 * is high-carbon on the grid average and near zero on a renewable contract.
 * Reporting one total and not saying which basis it used is the commonest way
 * a statement misleads without saying anything false, so there are two totals
 * and each is labelled.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Pure. Lines in, sums out.
 */

import { readFigure } from '@/domain/progress'

/**
 * Where a line sits in the inventory.
 *
 * Scope 1 is what the company burns, scope 2 the energy it buys, scope 3
 * everything else in its value chain. The two scope 2 bases are separate
 * buckets rather than a setting, because a statement reports both.
 */
export const GHG_SCOPES = ['scope1', 'scope2Location', 'scope2Market', 'scope3'] as const

export type GhgScope = (typeof GHG_SCOPES)[number]

/**
 * The fifteen categories of scope 3.
 *
 * A closed list, unlike a disclosure reference: the GHG Protocol defines
 * exactly these, upstream one to eight and downstream nine to fifteen, and a
 * sixteenth would be a mistake rather than something this does not know about.
 * Held as ids so their names can be translated; the numbering is the same in
 * every language and is what people cite.
 */
export const SCOPE3_CATEGORIES = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
  '12',
  '13',
  '14',
  '15',
] as const

export type Scope3Category = (typeof SCOPE3_CATEGORIES)[number]

/** Where the upstream categories stop and the downstream ones begin. */
export const SCOPE3_UPSTREAM_COUNT = 8

/** One line of the inventory. */
export interface GhgLine {
  readonly id: string
  scope: GhgScope
  /** Which of the fifteen, for a scope 3 line. Empty for anything else. */
  category: string
  /** What it is, where the category alone does not say. */
  label: string
  value: string
  comparative: string
}

/** What one bucket comes to. */
export interface ScopeSum {
  /** Null when no line of this scope carried a figure that could be read. */
  readonly value: number | null
  readonly comparative: number | null
  /** How many lines were skipped because their figure could not be read. */
  readonly unreadable: number
}

/** The whole inventory, added up. */
export interface GhgTotals {
  readonly scope1: ScopeSum
  readonly scope2Location: ScopeSum
  readonly scope2Market: ScopeSum
  readonly scope3: ScopeSum
  /** Scope 1 + scope 2 market-based + scope 3. */
  readonly totalMarket: ScopeSum
  /** Scope 1 + scope 2 location-based + scope 3. */
  readonly totalLocation: ScopeSum
}

/**
 * One bucket's total.
 *
 * A line whose figure cannot be read is skipped and counted rather than taken
 * as zero. Treating "n/a" as nothing would quietly understate an inventory,
 * and understating one is the failure this whole module exists to prevent.
 *
 * A bucket with no readable figure at all sums to null rather than to zero,
 * because zero emissions and nothing reported are very different claims.
 */
export function sumScope(lines: readonly GhgLine[], scope: GhgScope): ScopeSum {
  const mine = lines.filter((line) => line.scope === scope)

  let value: number | null = null
  let comparative: number | null = null
  let unreadable = 0

  for (const line of mine) {
    const current = readFigure(line.value)
    if (current === null) {
      if (line.value.trim() !== '') unreadable += 1
    } else {
      value = (value ?? 0) + current
    }

    const before = readFigure(line.comparative)
    if (before !== null) comparative = (comparative ?? 0) + before
  }

  return { value, comparative, unreadable }
}

/** Two sums added, keeping null where neither side had anything. */
function add(...sums: readonly ScopeSum[]): ScopeSum {
  let value: number | null = null
  let comparative: number | null = null
  let unreadable = 0

  for (const sum of sums) {
    if (sum.value !== null) value = (value ?? 0) + sum.value
    if (sum.comparative !== null) comparative = (comparative ?? 0) + sum.comparative
    unreadable += sum.unreadable
  }

  return { value, comparative, unreadable }
}

/** The inventory, added up both ways. */
export function ghgTotals(lines: readonly GhgLine[]): GhgTotals {
  const scope1 = sumScope(lines, 'scope1')
  const scope2Location = sumScope(lines, 'scope2Location')
  const scope2Market = sumScope(lines, 'scope2Market')
  const scope3 = sumScope(lines, 'scope3')

  return {
    scope1,
    scope2Location,
    scope2Market,
    scope3,
    totalMarket: add(scope1, scope2Market, scope3),
    totalLocation: add(scope1, scope2Location, scope3),
  }
}

/**
 * Emissions per unit of whatever the denominator is.
 *
 * Intensity is asked for alongside the gross figures because a total that
 * falls while the business doubles is not a reduction. Derived rather than
 * typed, like everything else here.
 */
export function intensityOf(total: number | null, denominator: string): number | null {
  const per = readFigure(denominator)
  if (total === null || per === null || per === 0) return null
  return total / per
}

/** Something about the inventory that will not hold up. */
export interface GhgProblem {
  readonly kind: 'unreadable' | 'noScope2Basis' | 'categoryTwice'
  /** The scope 3 category, where the problem is about one. */
  readonly category?: string
  readonly count?: number
}

/**
 * What an assurance provider would ask about.
 *
 * Not a validation that refuses anything: an inventory in progress is
 * incomplete by definition, and a builder that would not let somebody save one
 * would be useless. These are questions, raised where they can be seen.
 */
export function ghgProblems(lines: readonly GhgLine[]): readonly GhgProblem[] {
  const problems: GhgProblem[] = []

  const unreadable = lines.filter(
    (line) => line.value.trim() !== '' && readFigure(line.value) === null,
  ).length
  if (unreadable > 0) problems.push({ kind: 'unreadable', count: unreadable })

  // Both bases are required, and a statement reporting only one is the
  // commonest way an inventory misleads without saying anything false.
  const hasLocation = lines.some(
    (line) => line.scope === 'scope2Location' && line.value.trim() !== '',
  )
  const hasMarket = lines.some((line) => line.scope === 'scope2Market' && line.value.trim() !== '')
  if (hasLocation !== hasMarket) problems.push({ kind: 'noScope2Basis' })

  // A category counted twice doubles it, and the total still looks plausible.
  const seen = new Set<string>()
  for (const line of lines) {
    if (line.scope !== 'scope3' || line.category === '') continue
    if (seen.has(line.category)) {
      problems.push({ kind: 'categoryTwice', category: line.category })
    }
    seen.add(line.category)
  }

  return problems
}
